import { System } from '@ecs/System';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { TorpedoWeapon } from '@components/TorpedoWeapon';
import { Torpedo } from '@components/Torpedo';
import { Tag } from '@components/Tag';
import { AI } from '@components/AI';
import { Renderable } from '@components/Renderable';
import { Lifetime } from '@components/Lifetime';
import { Collider } from '@components/Collider';
import { BulletTrail } from '@components/BulletTrail';
import { Health } from '@components/Health';
import { WeaponUtils } from '@utils/WeaponUtils';
import { SoundManager, SoundType } from '@audio/SoundManager';
import { Effects } from '@/effects/Effects';

export class TorpedoWeaponSystem extends System {
  private soundManager = SoundManager.getInstance();
  private canvas!: HTMLCanvasElement;

  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
  }
  update(_deltaTime: number): void {
    const currentTime = Date.now();
    const torpedoShips = this.getEntitiesWithComponents(Position, Velocity, TorpedoWeapon, Tag);
    const players = this.getEntitiesWithComponents(Position, Tag).filter(
      e => e.getComponent(Tag)!.value === 'player'
    );

    if (players.length === 0) return;

    for (const ship of torpedoShips) {
      const tag = ship.getComponent(Tag)!;
      if (tag.value !== 'enemy') continue;

      const position = ship.getComponent(Position)!;
      const velocity = ship.getComponent(Velocity)!;
      const weapon = ship.getComponent(TorpedoWeapon)!;
      const ai = ship.getComponent(AI);

      // Check if weapon can fire
      if (currentTime - weapon.lastShot < weapon.cooldown) continue;
      
      // Only shoot if within viewport
      if (!WeaponUtils.isInViewport(position, this.canvas)) continue;

      // Find nearest player
      let nearestPlayer = null;
      let minDistance = 400; // Max torpedo launch range

      for (const player of players) {
        const playerPos = player.getComponent(Position)!;
        const dx = playerPos.x - position.x;
        const dy = playerPos.y - position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minDistance) {
          minDistance = distance;
          nearestPlayer = { entity: player, position: playerPos };
        }
      }

      if (nearestPlayer) {
        // Launch torpedoes from both sides
        const perpAngle1 = velocity.angle + Math.PI / 2; // Right side
        const perpAngle2 = velocity.angle - Math.PI / 2; // Left side
        
        // Create torpedoes
        this.createTorpedo(position, perpAngle1, weapon, nearestPlayer.entity.id);
        this.createTorpedo(position, perpAngle2, weapon, nearestPlayer.entity.id);
        
        // Play torpedo launch sound
        this.soundManager.playSound(SoundType.TORPEDO_LAUNCH, 0.6);
        
        // Update cooldown
        weapon.lastShot = currentTime;
        if (ai) {
          ai.lastShot = currentTime;
        }
        
        // Create launch effect
        this.createLaunchEffect(position, perpAngle1);
        this.createLaunchEffect(position, perpAngle2);
      }
    }
  }

  private createTorpedo(position: Position, launchAngle: number, weapon: TorpedoWeapon, targetId: number): void {
    const launchDistance = 20; // Distance from ship center
    const torpedo = new Entity()
      .addComponent(new Position(
        position.x + Math.cos(launchAngle) * launchDistance,
        position.y + Math.sin(launchAngle) * launchDistance
      ))
      .addComponent(new Velocity(launchAngle, 1)) // Start slow
      .addComponent(new Torpedo(targetId))
      .addComponent(new Renderable('torpedo', weapon.torpedoColor, 5, '#ffcc66', 1))
      .addComponent(new Health(3, 3)) // Can survive 3 hits from point defense
      .addComponent(new Lifetime(weapon.torpedoLifetime, weapon.torpedoLifetime))
      .addComponent(new Collider(6, 'enemyBullet', ['player']))
      .addComponent(new Tag('torpedo'))
      .addComponent(new BulletTrail());

    this.world.addEntity(torpedo);
  }

  private createLaunchEffect(position: Position, angle: number): void {
    const x = position.x + Math.cos(angle) * 15;
    const y = position.y + Math.sin(angle) * 15;
    Effects.smoke(this.world, x, y, 4, '#888888');
    Effects.muzzleFlash(this.world, x, y, angle, '#ff8800', 1.5);
  }
}
